import { Box } from '@material-ui/core';
import moment from 'moment';
import React from 'react';

import { formatPhoneReadable, parsePhone } from '../../../../helpers/helpers';
import { ReactComponent as CommentIcon } from '../../../shared/assets/icons/comment.svg';
import { ReactComponent as GlassesIcon } from '../../../shared/assets/icons/glasses.svg';
import { ReactComponent as PersonIcon } from '../../../shared/assets/icons/person.svg';
import { toTzMoment } from '../../../shared/lib';
import {
  getReservePersonName,
  isTableOccupied,
  isTableOpen,
  getReserveItemState,
  tableColors,
} from '../lib';
import { lightTheme } from '../lib/theme';
import { ArrowButton } from './arrowButton';
import { Duo } from './duo';
import { SolidButton } from './solidButton';
import { SvgIcon } from './svgIcon';
import { Tag } from './tag';

const { fg } = lightTheme;
const { color } = lightTheme.theme;

const formatDuration = (duration: moment.Duration): string => {
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  if (Math.abs(hours) > 0) {
    return `${hours}ч ${minutes}м`;
  }

  return `${minutes}м`;
};

export const TableWidget = ({
  table,
  onBack,
  onCancel,
  isCancelDisabled = false,
  onGuestArrived,
  isGuestArrivedDisabled = false,
  tableSchedule,
  restaurantTz,
  // compact = false,
}: {
  table: GM.ReservesSchemas.ReserveTableDTO;
  onBack: () => void;
  onCancel?: (scheduleReserve: GM.ReservesSchemas.ScheduleReserve) => void;
  isCancelDisabled?: boolean;
  onGuestArrived?: (payload: { tableId: number } | { reserveId: number }) => void;
  isGuestArrivedDisabled?: boolean;
  tableSchedule?: GM.ReservesSchemas.TableSchedule;
  restaurantTz?: string;
  // compact?: boolean;
}) => {
  const current = tableSchedule?.current;
  const next = tableSchedule?.next;

  let phone: string | null = null;
  if (current?.scheduleType === 'RESERVE' && current.phone) {
    const phoneParsed = parsePhone(current.phone);
    if (phoneParsed) {
      phone = formatPhoneReadable(phoneParsed);
    }
  }

  let person = 'Свободен';
  if (current) {
    person = (current.scheduleType === 'RESERVE' && getReservePersonName(current)) || 'Гость';
  }

  const comment = current?.scheduleType === 'RESERVE' && current.comment;
  const isBanquet = current?.scheduleType === 'RESERVE' && current.isBanquet;
  const isOccupied = current && isTableOccupied(current);
  const isAbilityToCancel = current?.scheduleType === 'RESERVE' && !isTableOpen(current);
  const state = current ? getReserveItemState(current, tableSchedule, restaurantTz) : undefined;
  const nextReserveState = next
    ? getReserveItemState(next, tableSchedule, restaurantTz)
    : undefined;

  let isAbilityToOccupy = !isOccupied;
  if (
    isAbilityToOccupy &&
    !(current?.scheduleType === 'RESERVE' && current?.isScheduled) &&
    ['WARNING', 'DANGER'].includes(nextReserveState)
  ) {
    isAbilityToOccupy = false;
  }

  const now = toTzMoment(undefined, restaurantTz);
  let tableDuration: React.ReactNode | null = null;
  if (current?.openedTableDate) {
    const diff = now.diff(toTzMoment(current.openedTableDate, restaurantTz));
    tableDuration = (
      <>
        Открыт
        <br />
        {formatDuration(moment.duration(diff))}
      </>
    );
  } else if (isOccupied) {
    const startDate_ = (current?.scheduleType === 'RESERVE' && current.arrivedDate) || current.date;
    const startDate = toTzMoment(startDate_, restaurantTz);

    // Замечена ситуация (ошибка?), когда время arrivedDate не задано
    //   и срабатывает fallback на date, которая еще не наступило,
    //   и получается отрицательная величина. В таких случаях не выводим
    if (now.isAfter(startDate)) {
      const diff = now.diff(startDate);
      tableDuration = (
        <>
          Занят
          <br />
          {formatDuration(moment.duration(diff))}
        </>
      );
    }
  }

  const tableDurationStyles: React.CSSProperties = {};

  if (tableDuration) {
    tableDurationStyles['color'] = tableColors[state].head.color;
    tableDurationStyles['background'] = tableColors[state].head.bg;

    if (tableColors[state].head.stripe) {
      tableDurationStyles['fontWeight'] = 600;
      tableDurationStyles['background'] = `repeating-linear-gradient(
            -45deg,
            ${tableColors[state].head.stripe} 0 4px,
            transparent 4px 8px
          ), ${tableDurationStyles['background']}`;
    }
  }

  return (
    <>
      <Duo
        first={
          <ArrowButton
            variant="left"
            onClick={onBack}
            style={{
              // background: 'rgb(81, 89, 102)',
              // color: '#FFFFFF',
              background: 'transparent',
              marginTop: '-8px',
              width: 40,
              height: 40,
            }}
          />
        }
        second={
          <Box width="100%" lineHeight={1}>
            <Box
              width="100%"
              display="flex"
              flexDirection="row"
              gridColumnGap={16}
              marginBottom={'16px'}
            >
              <Box width="33%">
                <Box fontSize={24} fontWeight={500} marginBottom="4px">
                  {table.number}
                </Box>
                <Box fontSize={12} fontWeight={400} color={fg.muted}>
                  {table.hall}
                </Box>
              </Box>
              <Box width="34%">
                <Duo
                  first={<SvgIcon icon={PersonIcon} width={22} height={22} />}
                  second={
                    <Box fontSize={24} fontWeight={500}>
                      {table.capacity}
                    </Box>
                  }
                  gridColumnGap={4}
                  alignItems="center"
                />
              </Box>
              <Box width="33%" textAlign="right">
                {tableDuration && (
                  <Box
                    display="inline-block"
                    borderRadius={8}
                    padding={'4px 8px'}
                    fontSize={12}
                    textAlign="right"
                    lineHeight={1.2}
                    style={tableDurationStyles}
                  >
                    {tableDuration}
                  </Box>
                )}
              </Box>
            </Box>

            <Box fontSize={24} fontWeight={500}>
              {person}
            </Box>

            {(phone || isBanquet || comment) && (
              <Box marginTop={'16px'}>
                {phone && (
                  <Box fontSize={14} fontWeight={600}>
                    {phone}
                  </Box>
                )}

                {isBanquet && (
                  <Box mt={phone ? '12px' : 0}>
                    <Tag
                      style={{
                        backgroundColor: color.neutral[100],
                        marginRight: '4px',
                        marginBottom: '4px',
                      }}
                    >
                      <Duo
                        first={<SvgIcon icon={GlassesIcon} width={14} height={14} />}
                        second={<span style={{ fontSize: 12, fontWeight: 600 }}>Банкет</span>}
                        gridColumnGap={4}
                        alignItems="center"
                      />
                    </Tag>
                  </Box>
                )}

                {comment && (
                  <Duo
                    first={<SvgIcon icon={CommentIcon} width={14} height={14} />}
                    second={
                      <Box lineHeight={1.2} color={fg.muted} fontSize={14} marginTop="-2px">
                        {comment}
                      </Box>
                    }
                    gridColumnGap={8}
                    mt={phone || isBanquet ? '12px' : 0}
                  />
                )}
              </Box>
            )}
          </Box>
        }
        gridColumnGap={16}
      />

      {onCancel && isAbilityToCancel && current.scheduleType === 'RESERVE' && (
        <Box mt="24px">
          <SolidButton
            onClick={() => onCancel(current)}
            disabled={isCancelDisabled}
            label="Освободить стол"
            style={{ width: '100%' }}
          />
        </Box>
      )}

      {onGuestArrived && isAbilityToOccupy && (
        <Box mt="24px">
          <SolidButton
            onClick={() => {
              if (current) {
                onGuestArrived({ reserveId: current.id });
              } else {
                onGuestArrived({ tableId: table.id });
              }
            }}
            disabled={isGuestArrivedDisabled}
            label={current ? 'Гость пришел' : 'Занять стол'}
            style={{ width: '100%' }}
          />
        </Box>
      )}
    </>
  );
};
